import React, { useState } from "react";
import { Formik, Field, Form } from "formik";
import { Button, Card, CardBody, FormGroup, Row, Col } from "reactstrap";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useParams } from "react-router";
import FormikInputField from "components/common/Fields/FormikInputField";
import FormikCheckboxField from "components/common/Fields/FormikCheckboxField";
import Loader from "components/Loader";
import API from "utils/API";
import { dateFormate } from "utils/columns";

const phoneRegExp =
  /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;

const validationSchema = Yup.object({
  faxNumber: Yup.string()
    .required("This field is Required")
    .min(10, "Must be exactly 10 digits")
    .max(10, "Must be exactly 10 digits")
    .matches(
      /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/,
      "Fax number is not valid"
    ),
});

const Fax = (props) => {
  if (loading) return <Loader opacity={0.1} color="black" />;
  const {
    claimID,
    patientFirstName,
    patientLastName,
    patientDOB,
    providerID,
    providerFirstName,
    providerLastName,
    providerSpecialty,
    providerStatus,
    rxNumber,
    faxNumber,
    refillNumber,
    dateRXWritten,
    dateofService,
  } = props.claim;
  const { onClose } = props;

  const [loading, setLoading] = useState(false);
  const { tenantID } = useParams();

  const initialValues = {
    faxNumber: faxNumber,
    updateFaxNumber: false,
    comments: "",
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    const payload = { claimID, ...values };
    try {
      await API.post(`/${tenantID}/fax/send`, payload);
      toast.success("Updated Successfully!");
      setSubmitting(false);
      onClose();
    } catch (error) {
      setSubmitting(false);
      toast.error("An error occurred");
    }
  };

  return (
    <Card className="border-0 rounded shadow-top">
      <CardBody>
        <Formik
          enableReinitialize={true}
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
          children={({ values, isSubmitting, isValid }) => {
            return (
              <Form>
                {isSubmitting && <Loader opacity={0.01} color="black" />}
                <Row className="align-items-center mb-3">
                  <Col sm="4">
                    <p className="f-14 mb-0">
                      Patient First Name:
                      <b className="d-inline-block ps-2 font-weight-500">
                        {patientFirstName}
                      </b>
                    </p>
                  </Col>
                  <Col sm="4">
                    <p className="f-14 mb-0">
                      Patient Last Name:
                      <b className="d-inline-block ps-2 font-weight-500">
                        {patientLastName}
                      </b>
                    </p>
                  </Col>
                  <Col sm="4">
                    <p className="f-14 mb-0">
                      Patient DOB:
                      <b className="d-inline-block ps-2 font-weight-500">
                        {dateFormate(patientDOB)}
                      </b>
                    </p>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col sm="4">
                    <p className="f-14 mb-0">
                      Provider NPI:
                      <b className="d-inline-block ps-2 font-weight-500">
                        {providerID}
                      </b>
                    </p>
                  </Col>
                  <Col sm="4">
                    <p className="f-14 mb-0">
                      Provider First Name:
                      <b className="d-inline-block ps-2 font-weight-500">
                        {providerFirstName}
                      </b>
                    </p>
                  </Col>
                  <Col sm="4">
                    <p className="f-14 mb-0">
                      Provider Last Name:
                      <b className="d-inline-block ps-2 font-weight-500">
                        {providerLastName}
                      </b>
                    </p>
                  </Col>
                  <Col sm="4">
                    <p className="f-14 mb-0">
                      Provider Specialty:
                      <b className="d-inline-block ps-2 font-weight-500">
                        {providerSpecialty}
                      </b>
                    </p>
                  </Col>
                  <Col sm="4">
                    <p className="f-14 mb-0">
                      Provider Status:
                      <b className="d-inline-block ps-2 font-weight-500">
                        {providerStatus}
                      </b>
                    </p>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col sm="4">
                    <p className="f-14 mb-0">
                      RX Number:
                      <b className="d-inline-block ps-2 font-weight-500">
                        {rxNumber}
                      </b>
                    </p>
                  </Col>
                  <Col sm="4">
                    <p className="f-14 mb-0">
                      Refill Number:
                      <b className="d-inline-block ps-2 font-weight-500">
                        {refillNumber}
                      </b>
                    </p>
                  </Col>
                  <Col sm="4">
                    <p className="f-14 mb-0">
                      Date Rx Written:
                      <b className="d-inline-block ps-2 font-weight-500">
                        {dateFormate(dateRXWritten)}
                      </b>
                    </p>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <p className="f-14 mb-0">
                      Date of Service:
                      <b className="d-inline-block ps-2 font-weight-500">
                        {dateFormate(dateofService)}
                      </b>
                    </p>
                  </Col>
                </Row>
                <hr />
                <Row>
                  <Col sm="4">
                    <FormGroup>
                      <Field
                        key="faxNumber"
                        id="faxNumber"
                        name="faxNumber"
                        type="text"
                        component={FormikInputField}
                        label="Fax Number"
                        autoComplete="off"
                      />
                    </FormGroup>
                  </Col>
                  <Col sm="12">
                    <FormGroup>
                      <Field
                        key="updateFaxNumber"
                        id="updateFaxNumber"
                        name="updateFaxNumber"
                        type="checkbox"
                        component={FormikCheckboxField}
                        label="Use always fax "
                      />
                    </FormGroup>
                  </Col>
                  <Col sm="12">
                    <FormGroup>
                      <Field
                        key="comments"
                        id="comments"
                        name="comments"
                        type="textarea"
                        component={FormikInputField}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <div className="d-flex justify-content-end align-items-center">
                  <div>
                    <Button
                      color="primary"
                      type="submit"
                      size="sm"
                      disabled={!isValid || isSubmitting}
                    >
                      Send
                    </Button>
                  </div>
                </div>
              </Form>
            );
          }}
        ></Formik>
      </CardBody>
    </Card>
  );
};

export default Fax;
